/**
 * Formats a large number with K/M/B/T suffixes for compact display
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with suffix (e.g., "1.5K", "2.3M")
 */
export const formatLargeNumber = (value: number, decimals = 2): string => {
  const absValue = Math.abs(value)
  const sign = value < 0 ? '-' : ''

  if (absValue >= 1_000_000_000_000) {
    // Trillions
    return `${sign}${(absValue / 1_000_000_000_000).toFixed(decimals)}T`
  } else if (absValue >= 1_000_000_000) {
    // Billions
    return `${sign}${(absValue / 1_000_000_000).toFixed(decimals)}B`
  } else if (absValue >= 1_000_000) {
    // Millions
    return `${sign}${(absValue / 1_000_000).toFixed(decimals)}M`
  } else if (absValue >= 1_000) {
    // Thousands
    return `${sign}${(absValue / 1_000).toFixed(decimals)}K`
  }

  return value.toFixed(decimals)
}

/**
 * Formats a number with thousand separators (commas)
 * @param value - The number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with commas (e.g., "1,234,567.89")
 */
export const formatNumberWithCommas = (
  value: number,
  formatParams?: { minDecimalPlaces?: number; maxDecimalPlaces?: number }
): string => {
  const { minDecimalPlaces = 2, maxDecimalPlaces = 2 } = formatParams || {}

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: minDecimalPlaces,
    maximumFractionDigits: maxDecimalPlaces,
  }).format(value)
}
